import React, { useMemo } from "react";

function countPlaceholders(text) {
	if (!text) return 0;
	return (String(text).match(/\{\{\d+\}\}/g) || []).length;
}

function fillComponentText(text, params, startIndex) {
	const source = text || "";
	const count = countPlaceholders(source);
	let filled = source;
	for (let i = 0; i < count; i += 1) {
		const raw = params[startIndex + i];
		const display =
			!raw || !String(raw).trim()
				? `{{${i + 1}}}`
				: raw === "contactName"
					? "Nome do contato"
					: String(raw);
		filled = filled.replace(`{{${i + 1}}}`, display);
	}
	return { filled, nextIndex: startIndex + count };
}

function getComponent(template, type) {
	return template?.components?.find(
		(c) => String(c?.type || "").toUpperCase() === type
	);
}

/**
 * Preview estilo bolha do WhatsApp (mesmo visual do modal de envio WABA).
 */
export default function TemplateConversationPreview({
	template,
	params = [],
	mediaPath,
	darkMode = false,
}) {
	const preview = useMemo(() => {
		if (!template) return null;

		const header = getComponent(template, "HEADER");
		const body = getComponent(template, "BODY");
		const footer = getComponent(template, "FOOTER");
		const buttonsComp = getComponent(template, "BUTTONS");
		const headerFormat = String(header?.format || "TEXT").toUpperCase();

		let idx = 0;
		let headerText = "";
		if (headerFormat === "TEXT" || headerFormat === "") {
			const result = fillComponentText(header?.text || "", params, idx);
			headerText = result.filled;
			idx = result.nextIndex;
		}

		const bodyResult = fillComponentText(body?.text || "", params, idx);
		idx = bodyResult.nextIndex;

		const buttons = Array.isArray(buttonsComp?.buttons)
			? buttonsComp.buttons.map((btn) => {
					const type = String(btn?.type || "").toUpperCase();
					if (type === "URL" && countPlaceholders(btn.url || "") > 0) {
						const result = fillComponentText(btn.url || "", params, idx);
						idx = result.nextIndex;
						return {
							type,
							label: btn.text || "Abrir link",
							urlPreview: result.filled,
						};
					}
					return {
						type,
						label: btn.text || "Botão",
						urlPreview: btn.url || "",
					};
				})
			: [];

		return {
			headerFormat,
			headerText,
			bodyText: bodyResult.filled,
			footerText: footer?.text || "",
			buttons,
		};
	}, [template, params]);

	const chatBg = darkMode ? "#0b141a" : "#efeae2";
	const bubbleBg = darkMode ? "#005c4b" : "#d9fdd3";
	const bubbleText = darkMode ? "#e9edef" : "#111b21";
	const mutedText = darkMode ? "#8696a0" : "#667781";
	const headerBar = darkMode ? "#1f2c34" : "#008069";

	if (!preview) {
		return (
			<div
				className="flex h-full min-h-[260px] items-center justify-center rounded-xl border border-dashed p-6 text-center"
				style={{
					borderColor: darkMode ? "#3f3f46" : "#d4d4d8",
					background: darkMode ? "#18181b" : "#fafafa",
					color: mutedText,
				}}
			>
				<p className="m-0 text-sm">
					Selecione um template para ver o preview da conversa
				</p>
			</div>
		);
	}

	return (
		<div
			className="flex h-full min-h-[280px] w-full flex-col overflow-hidden rounded-xl border"
			style={{ borderColor: darkMode ? "#3f3f46" : "#e4e4e7" }}
		>
			<div className="shrink-0 px-4 py-3 text-white" style={{ background: headerBar }}>
				<p className="m-0 text-sm font-semibold">Preview da conversa</p>
				<p className="m-0 text-xs opacity-80">
					{(template.name || template.elementName || template.title || "").replace(
						/_/g,
						" "
					)}
				</p>
			</div>

			<div
				className="flex flex-1 items-center justify-center overflow-auto p-5"
				style={{ background: chatBg }}
			>
				<div
					className="w-full max-w-[280px] overflow-hidden rounded-2xl shadow-md"
					style={{ background: bubbleBg, color: bubbleText }}
				>
					{preview.headerFormat === "IMAGE" && (
						<div style={{ background: darkMode ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.04)" }}>
							{mediaPath ? (
								<img
									src={mediaPath}
									alt="Preview do template"
									className="max-h-48 w-full object-cover"
								/>
							) : (
								<div
									className="flex h-36 items-center justify-center text-xs"
									style={{ color: mutedText }}
								>
									Imagem do template
								</div>
							)}
						</div>
					)}

					{preview.headerFormat === "VIDEO" && (
						<div
							className="flex h-28 items-center justify-center text-xs"
							style={{
								background: darkMode ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.06)",
								color: mutedText,
							}}
						>
							{mediaPath ? "Vídeo anexado" : "Vídeo do template"}
						</div>
					)}

					{preview.headerFormat === "DOCUMENT" && (
						<div
							className="flex h-14 items-center gap-2 px-3 text-xs"
							style={{
								background: darkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
							}}
						>
							<span className="inline-flex size-8 items-center justify-center rounded bg-red-500/20 font-semibold text-red-500">
								PDF
							</span>
							<span className="truncate">
								{mediaPath ? "Documento anexado" : "Documento do template"}
							</span>
						</div>
					)}

					<div className="space-y-1.5 px-3.5 py-3">
						{preview.headerText ? (
							<p className="m-0 whitespace-pre-wrap text-sm font-semibold">
								{preview.headerText}
							</p>
						) : null}

						{preview.bodyText ? (
							<p className="m-0 whitespace-pre-wrap text-sm leading-relaxed">
								{preview.bodyText}
							</p>
						) : null}

						{preview.footerText ? (
							<p className="m-0 text-xs" style={{ color: mutedText }}>
								{preview.footerText}
							</p>
						) : null}

						<p className="m-0 text-right text-[10px]" style={{ color: mutedText }}>
							agora
						</p>
					</div>

					{preview.buttons.length > 0 && (
						<div
							style={{
								borderTop: `1px solid ${
									darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
								}`,
							}}
						>
							{preview.buttons.map((btn, index) => (
								<div
									key={`${btn.label}-${index}`}
									className="px-3 py-2.5 text-center text-sm font-medium"
									style={{
										color: darkMode ? "#53bdeb" : "#027eb5",
										borderBottom:
											index < preview.buttons.length - 1
												? `1px solid ${
														darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
													}`
												: "none",
									}}
									title={btn.urlPreview || btn.label}
								>
									{btn.type === "URL" ? `🔗 ${btn.label}` : btn.label}
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
